// Thin fetch wrapper for the conciliatron API. Injects the gateway-claims
// auth headers (X-Authz-User-Id / X-Authz-Workspace-Ids) the backend reads
// off the request context. In the lean local stack there's no gateway, so
// the frontend sends these directly; behind the real API Gateway the
// authorizer overwrites them. Base URL + identity are configurable at
// runtime (mirrors storage-hub's `configure()`), defaulting from ENV — which
// resolves the deploy-written /config.js first, then VITE_* for local dev.
//
// When getAccessToken is registered (by AuthBridge in main.tsx), every request
// carries Authorization: Bearer <token>. When the backend rejects that token
// mid-session (401, or a 403 that is not an RBAC "forbidden" denial — see
// isAuthRecoverable), the token is force-refreshed and the request retried once;
// a second such failure calls onAuthFailure (which triggers signOut). A genuine
// 403 "forbidden" surfaces as an error without refresh or sign-out.

import { isAuthRecoverable, DEV_TOKEN } from '@micronet/auth';
import { gzipJsonBody } from '@micronet/gzip';
import { ENV } from '../config/env';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface ApiClientConfig {
  baseUrl: string;
  authzUserId?: string;
  authzWorkspaceIds?: string;
  getAccessToken?: (forceRefresh?: boolean) => Promise<string>;
  onAuthFailure?: (error?: string) => void;
}

// Only in force until useStore.init() calls configure(); it still derives from
// ENV so there is one source for the base and no hardcoded host in the bundle.
//
// authzWorkspaceIds has no '1' fallback: it is a claim about who the caller is, and
// asserting a workspace before init() has resolved one from /me is how a deployed
// build ended up naming workspace 1 on its first request. Unset means the header is
// omitted (see the guard where the headers are built) — in a deployed env the
// gateway overwrites it from the authorizer anyway, and the lean local stack has
// VITE_AUTHZ_WORKSPACE_IDS.
let config: ApiClientConfig = {
  baseUrl: ENV.conciliatronApi,
  authzUserId: ENV.authzUserId,
  authzWorkspaceIds: import.meta.env.VITE_AUTHZ_WORKSPACE_IDS,
};

// A request that neither resolves nor rejects would lock its caller forever —
// e.g. an execution poll or the dispatch POST leaving an infinite spinner. Abort
// any request that outlives this budget so a stalled connection surfaces as a
// retryable error instead of a hang. The dispatch only enqueues and every poll
// GET is small, so a single generous budget is enough.
const REQUEST_TIMEOUT_MS = 30_000;

export function configure(next: Partial<ApiClientConfig>): void {
  config = { ...config, ...next };
}

/**
 * The registered token getter, for handing conciliatron's identity to ANOTHER
 * module's API client (storage-hub's, when uploading the report PDF).
 *
 * Read through a function, never captured: AuthBridge registers the real getter
 * after the first render, so a value read at module scope would be the
 * pre-registration `undefined` forever — which is the 401 on the first upload of
 * every session.
 */
export function currentAccessTokenGetter():
  | ((forceRefresh?: boolean) => Promise<string>)
  | undefined {
  return config.getAccessToken;
}

// No Content-Type here: gzipJsonBody in request() owns it, together with
// Content-Encoding, so the two can never disagree. A bodyless request still
// sends neither.
async function buildHeaders(forceRefresh = false): Promise<Record<string, string>> {
  const h: Record<string, string> = {};
  if (config.authzUserId) h['X-Authz-User-Id'] = config.authzUserId;
  if (config.authzWorkspaceIds) h['X-Authz-Workspace-Ids'] = config.authzWorkspaceIds;
  if (config.getAccessToken) {
    const token = await config.getAccessToken(forceRefresh);
    if (token && token !== DEV_TOKEN) h['Authorization'] = `Bearer ${token}`;
  }
  return h;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false
): Promise<T> {
  const hasBody = body !== undefined;
  const headers = await buildHeaders(isRetry);

  // Re-encoded on the retry below rather than reused: an ArrayBuffer body is
  // consumed by the first fetch.
  let payload: BodyInit | undefined;
  if (hasBody) {
    const encoded = await gzipJsonBody(body);
    payload = encoded.body;
    Object.assign(headers, encoded.headers);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(config.baseUrl + path, {
      method,
      headers,
      body: payload,
      signal: controller.signal,
    });
  } catch (e) {
    // The timeout above aborts the fetch, which rejects with an AbortError.
    // Translate it to a distinguishable ApiError (status 0) so callers can treat
    // it as transient (the poller retries; one-shot callers surface it) rather
    // than leaking a raw DOMException.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ApiError(0, 'timeout', 'La solicitud tardó demasiado. Intenta nuevamente.');
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // Non-OK: parse the structured {code,message} error body once — it feeds both
  // the refresh decision (the 403 discriminator) and the thrown ApiError.
  let code = 'error';
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const parsed = (await res.json()) as { code?: string; message?: string };
    if (parsed.code) code = parsed.code;
    if (parsed.message) message = parsed.message;
  } catch {
    // non-JSON error body — keep the status-derived message
  }

  // Reactive token refresh: if the backend rejected our bearer mid-session,
  // force a fresh token and retry once. Gated on a bearer actually having been
  // sent (`Authorization` present) so the dev-bypass sentinel — which sends no
  // bearer — never triggers a refresh or a sign-out on a genuine dev 403.
  const sentBearer = headers['Authorization'] !== undefined;
  if (sentBearer && isAuthRecoverable(res.status, code)) {
    if (!isRetry) {
      return request<T>(method, path, body, true);
    }
    config.onAuthFailure?.('Your session could not be verified. Please sign in again.');
  }

  throw new ApiError(res.status, code, message);
}

export const apiClient = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  delete: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};
